import _extends from '@babel/runtime/helpers/extends';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { forwardRef, useState, useMemo, useEffect, Children, isValidElement, cloneElement } from 'react';
import classNames from 'classnames';
import { useId } from '@andes/context';
import { ListItemContent, List } from '@andes/list';
import _jsx from '@babel/runtime/helpers/jsx';
import { RadioButton } from '@andes/radio-button';
import includes from '@andes/common/build/utils/includes';
import warning from '@andes/common/build/utils/warning';
import getListItemClassNames from '@andes/list/build/utils/getListItemClassNames';
const namespace = 'andes-radio-list';
const listItemNamespace = 'andes-list__item';
const RADIO_LIST_DISPLAY_NAME = 'RadioList';
const RADIO_LIST_ITEM_DISPLAY_NAME = 'RadioListItem';
const RL_SUPPORTED_ITEM_TYPES = ['ListItem', 'RadioListItem'];
const RL_SUPPORTED_GROUP_TYPES = ['ListGroup'];
const WARNING_BUTTON_SWITCH = '<RadioListItem> does not allow Buttons or Switches as rightContent';
const RadioListItem = /*#__PURE__*/function () {
  const RadioListItem = /*#__PURE__*/forwardRef((props, ref) => {
    var _props$value;
    const {
      currentValue,
      disabled = false,
      disableSelectionComponent = false,
      error = false,
      id,
      name,
      onClick,
      side = 'left',
      size = 'medium',
      value,
      title,
      description,
      rightContent,
      leftContent,
      rightContentPosition = 'top',
      primary: _willdeprecate$primary,
      secondary: _willdeprecate$secondary,
      tertiary: _willdeprecate$tertiary,
      image: _willdeprecate$image,
      tertiaryPosition: _willdeprecate$tertiaryPosition
    } = props;
    const computedTitle = title || _willdeprecate$primary;
    const computedDescription = description || _willdeprecate$secondary;
    const computedRightContent = rightContent || _willdeprecate$tertiary;
    const computedLeftContent = leftContent || _willdeprecate$image;
    const computedRightContentPosition = _willdeprecate$tertiaryPosition || rightContentPosition;
    const listItemClassNames = getListItemClassNames(_objectSpread(_objectSpread({
      size
    }, props), {}, {
      title: computedTitle,
      description: computedDescription,
      rightContent: computedRightContent,
      leftContent: computedLeftContent,
      rightContentPosition: computedRightContentPosition
    }));
    const getRadio = () => {
      const disableSelection = disabled || disableSelectionComponent;
      const radioProps = {
        checked: (currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString()) === (value === null || value === void 0 ? void 0 : value.toString()),
        disabled: disableSelection,
        id: "".concat(id, "-selection-component-").concat(value),
        value: (value === null || value === void 0 ? void 0 : value.toString()) || '',
        name,
        error
      };
      return /*#__PURE__*/_jsx("div", {
        className: "".concat(listItemNamespace, "-selection-control")
      }, void 0, /*#__PURE__*/React.createElement(RadioButton, radioProps));
    };
    const getVerifiedRightContent = () => {
      var _computedRightContent;
      if (includes(['Button', 'Switch'], computedRightContent === null || computedRightContent === void 0 || (_computedRightContent = computedRightContent.type) === null || _computedRightContent === void 0 ? void 0 : _computedRightContent.displayName)) {
        warning(false, WARNING_BUTTON_SWITCH);
        return undefined;
      }
      return computedRightContent;
    };
    return /*#__PURE__*/React.createElement("li", {
      className: listItemClassNames,
      onClick: onClick,
      ref: ref
    }, side === 'left' && getRadio(), /*#__PURE__*/React.createElement(ListItemContent, _extends({}, props, {
      size: size,
      value: (_props$value = props.value) === null || _props$value === void 0 ? void 0 : _props$value.toString(),
      title: computedTitle,
      description: computedDescription,
      rightContent: getVerifiedRightContent(),
      leftContent: computedLeftContent,
      rightContentPosition: computedRightContentPosition
    })), side === 'right' && getRadio());
  });
  RadioListItem.displayName = RADIO_LIST_ITEM_DISPLAY_NAME;
  return RadioListItem;
}();
var RadioListItem$1 = RadioListItem;
const _excluded = ["children", "className", "defaultValue", "id", "name", "onChange", "onClick", "onSelect", "side", "value"];
const RadioList = /*#__PURE__*/function () {
  const RadioList = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        children,
        className,
        defaultValue,
        id,
        name,
        onChange,
        onClick: _willdeprecate$onClick,
        onSelect: _willdeprecate$onSelect,
        side,
        value
      } = _ref,
      listProps = _objectWithoutProperties(_ref, _excluded);
    const componentId = useId(id);
    const [currentValue, setCurrentValue] = useState(value || defaultValue);
    const isUncontrolled = useMemo(() => value === undefined, []);
    useEffect(() => {
      if (!isUncontrolled) {
        setCurrentValue(value);
      }
    }, [value]);
    const radioListClassNames = classNames(namespace, className);
    const customOnSelect = (event, itemDetails) => {
      const {
        value: itemValue = '',
        disabled,
        disableSelectionComponent
      } = itemDetails;
      if (disabled || disableSelectionComponent) return;
      if (isUncontrolled) setCurrentValue(itemValue === null || itemValue === void 0 ? void 0 : itemValue.toString());
      if (typeof onChange === 'function') onChange(event, itemValue);
      if (typeof _willdeprecate$onClick === 'function') _willdeprecate$onClick(event, itemValue);
      if (typeof _willdeprecate$onSelect === 'function') _willdeprecate$onSelect(event, itemValue);
    };
    const renderItem = child => {
      var _child$type;
      const commonProps = _objectSpread(_objectSpread({}, child.props), {}, {
        addPrimaryLabel: true,
        currentValue,
        name: name || "".concat(componentId, "-radio"),
        side,
        onClick: event => customOnSelect(event, child.props)
      });
      if (((_child$type = child.type) === null || _child$type === void 0 ? void 0 : _child$type.displayName) === 'ListItem') return /*#__PURE__*/React.createElement(RadioListItem$1, commonProps);
      return /*#__PURE__*/cloneElement(child, _objectSpread({}, commonProps));
    };
    return /*#__PURE__*/React.createElement(List, _extends({
      className: radioListClassNames,
      ref: ref,
      id: componentId
    }, listProps), Children.map(children, child => {
      var _child$type2, _child$type3, _child$type4, _child$type5;
      if (includes(RL_SUPPORTED_GROUP_TYPES, (_child$type2 = child.type) === null || _child$type2 === void 0 ? void 0 : _child$type2.displayName) || includes(RL_SUPPORTED_GROUP_TYPES, (_child$type3 = child.type) === null || _child$type3 === void 0 || (_child$type3 = _child$type3.wraps) === null || _child$type3 === void 0 ? void 0 : _child$type3.displayName)) {
        const enhancedItems = Children.map(child === null || child === void 0 ? void 0 : child.props.children, item => renderItem(item));
        if (/*#__PURE__*/isValidElement(child)) {
          return /*#__PURE__*/cloneElement(child, {
            children: enhancedItems
          });
        }
      }
      if (includes(RL_SUPPORTED_ITEM_TYPES, (_child$type4 = child.type) === null || _child$type4 === void 0 ? void 0 : _child$type4.displayName) || includes(RL_SUPPORTED_ITEM_TYPES, (_child$type5 = child.type) === null || _child$type5 === void 0 || (_child$type5 = _child$type5.wraps) === null || _child$type5 === void 0 ? void 0 : _child$type5.displayName)) {
        if (/*#__PURE__*/isValidElement(child)) {
          return renderItem(child);
        }
      }
    }));
  });
  RadioList.displayName = RADIO_LIST_DISPLAY_NAME;
  return RadioList;
}();
var RadioList$1 = RadioList;
export { RadioList$1 as RadioList, RadioListItem$1 as RadioListItem, RadioList$1 as default };
